import React, { useEffect, useRef, useState } from "react";
import api, { WS_BASE } from "@/lib/api";
import { PaperPlaneRight, Paperclip, ImageSquare } from "@phosphor-icons/react";
import { toast } from "sonner";

const IMG_MIME = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/heic", "image/heif"];
const IMG_MAX_BYTES = 8 * 1024 * 1024;

export default function ChatPanel({ activityId, currentUser }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [connected, setConnected] = useState(false);
  const [uploading, setUploading] = useState(false);
  const wsRef = useRef(null);
  const endRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    let stopped = false;
    (async () => {
      try {
        const { data } = await api.get(`/activities/${activityId}/messages`);
        if (!stopped) setMessages(data || []);
      } catch {}
    })();

    const token = localStorage.getItem("tf_token") || "";
    const ws = new WebSocket(`${WS_BASE}/api/ws/chat/${activityId}?token=${encodeURIComponent(token)}`);
    wsRef.current = ws;
    ws.onopen = () => setConnected(true);
    ws.onclose = () => setConnected(false);
    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        // Dedup by message_id — image messages are saved via REST + broadcast,
        // so the sender can race and receive their own message twice.
        setMessages((m) => (m.some((x) => x.message_id === msg.message_id) ? m : [...m, msg]));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("chat ws bad payload", err?.message);
      }
    };
    return () => {
      stopped = true;
      try { ws.close(); } catch (err) {
        // eslint-disable-next-line no-console
        console.debug("chat ws close failed", err?.message);
      }
    };
  }, [activityId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t || !wsRef.current || wsRef.current.readyState !== 1) return;
    wsRef.current.send(JSON.stringify({ text: t }));
    setText("");
  };

  const pickImage = () => fileRef.current?.click();

  const onImageSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so the same file can be re-selected
    if (!file) return;
    if (!IMG_MIME.includes(file.type)) {
      toast.error("Use JPEG, PNG, WEBP, GIF or HEIC");
      return;
    }
    if (file.size > IMG_MAX_BYTES) {
      toast.error("Image is too large (max 8 MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const headers = { "Content-Type": "multipart/form-data" };
      const caption = text.trim();
      if (caption) headers["X-Caption"] = caption;
      const { data: saved } = await api.post(
        `/activities/${activityId}/messages/image`,
        fd,
        { headers },
      );
      // The REST endpoint also broadcasts on the WS, but if the WS is closed
      // (e.g. mobile background) we still want the sender to see the message.
      setMessages((m) => (m.some((x) => x.message_id === saved.message_id) ? m : [...m, saved]));
      setText("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't upload image");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="border border-[#262626] bg-[#141414] flex flex-col h-[480px]">
      <div className="px-4 py-3 border-b border-[#262626] flex items-center justify-between">
        <div className="label-overline">Group Chat</div>
        <div className="flex items-center gap-2 text-[10px] tracking-widest uppercase">
          <span className={`w-2 h-2 rounded-full ${connected ? "bg-[#34C759]" : "bg-zinc-600"}`} />
          <span className="text-zinc-400">{connected ? "Live" : "Connecting"}</span>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
        {messages.length === 0 && <div className="text-sm text-zinc-500">Say hello to your training partners.</div>}
        {messages.map((m) => {
          const mine = m.user_id === currentUser?.user_id;
          const imgUrl = m.image_url
            ? (m.image_url.startsWith("http") ? m.image_url : `${process.env.REACT_APP_BACKEND_URL}${m.image_url}`)
            : null;
          return (
            <div key={m.message_id} className={`flex gap-2 ${mine ? "justify-end" : ""}`}>
              {!mine && <Avatar name={m.user_name} src={m.user_picture} />}
              <div className={`max-w-[70%] px-3 py-2 ${mine ? "bg-[#007AFF] text-white" : "bg-[#1A1A1A] border border-[#262626]"}`}>
                {!mine && <div className="text-[10px] tracking-widest uppercase text-zinc-400 mb-0.5">{m.user_name}</div>}
                {imgUrl && (
                  <a
                    href={imgUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="chat-image-attachment"
                    className="block -mx-3 -mt-2 mb-1"
                  >
                    <img
                      src={imgUrl}
                      alt={m.text || "shared image"}
                      className="max-h-64 w-auto object-cover"
                      loading="lazy"
                    />
                  </a>
                )}
                {m.text && (
                  <div className="text-sm leading-snug whitespace-pre-wrap break-words">{m.text}</div>
                )}
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="border-t border-[#262626] p-3 flex gap-2">
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif"
          hidden
          ref={fileRef}
          onChange={onImageSelected}
          data-testid="chat-image-input"
        />
        <button
          type="button"
          onClick={pickImage}
          disabled={uploading}
          data-testid="chat-attach-image"
          aria-label="Attach image"
          className="kinetic-btn border border-[#262626] hover:border-white/40 bg-[#0A0A0A] px-3 disabled:opacity-50 flex items-center justify-center"
        >
          {uploading ? <ImageSquare weight="duotone" size={18} className="animate-pulse" /> : <Paperclip weight="bold" size={18} />}
        </button>
        <input
          data-testid="chat-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Write a message…"
          className="flex-1 bg-[#0A0A0A] border border-[#262626] px-3 py-2 text-sm focus:border-[#007AFF] outline-none"
        />
        <button data-testid="chat-send" type="submit" className="kinetic-btn bg-[#007AFF] hover:bg-[#0a66d0] text-white px-3">
          <PaperPlaneRight weight="bold" size={18} />
        </button>
      </form>
    </div>
  );
}

function Avatar({ name, src }) {
  const initials = (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  if (src) {
    const url = src.startsWith("http") ? src : `${process.env.REACT_APP_BACKEND_URL}${src}`;
    return <img src={url} alt="" className="w-7 h-7 object-cover" />;
  }
  return <div className="w-7 h-7 bg-[#1A1A1A] border border-[#262626] text-[10px] flex items-center justify-center text-zinc-300">{initials}</div>;
}
